import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, CheckCheck, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton, Textarea } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { MonitoringEventDetail, MonitoringEventItem, Paginated } from '@/lib/types';
import { cn, formatDateTime } from '@/lib/utils';

/** Zabbix-Schweregrade mit den Standardfarben von Zabbix. */
export const SEVERITIES = [
  { label: 'Nicht klassifiziert', color: '#97AAB3' },
  { label: 'Information', color: '#7499FF' },
  { label: 'Warnung', color: '#FFC859' },
  { label: 'Durchschnitt', color: '#FFA059' },
  { label: 'Hoch', color: '#E97659' },
  { label: 'Katastrophe', color: '#E45959' },
] as const;

export function SeverityBadge({ severity, className }: { severity: number; className?: string }) {
  const s = SEVERITIES[severity] ?? SEVERITIES[0];
  return (
    <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap text-slate-900', className)} style={{ background: s.color }}>
      {s.label}
    </span>
  );
}

export function duration(from: string, to?: string | null) {
  const min = Math.max(0, Math.round(((to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime()) / 60_000));
  if (min < 60) return `${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} Std. ${min % 60} Min.`;
  return `${Math.floor(h / 24)} T. ${h % 24} Std.`;
}

const HISTORY_LABELS: Record<string, string> = {
  problem: 'Problem',
  recovery: 'Behoben',
  update: 'Aktualisierung',
  acknowledged: 'Bestätigt',
  ticket: 'Ticket',
};

/** Ereignisdetails mit Verlauf und Bestätigung an Zabbix. */
export function EventDialog({ eventId, onClose }: { eventId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [message, setMessage] = useState('');
  const { data: e } = useQuery({
    queryKey: ['monitoring', 'event', eventId],
    queryFn: () => api<MonitoringEventDetail>(`/monitoring/events/${eventId}`),
    enabled: eventId !== null,
  });
  const ack = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>(`/monitoring/events/${eventId}/acknowledge`, { method: 'POST', body: { message } }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(r.message);
        setMessage('');
        qc.invalidateQueries({ queryKey: ['monitoring'] });
        qc.invalidateQueries({ queryKey: ['tickets'] });
      } else toast.error(r.message);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog open={eventId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle className="pr-8">{e?.name ?? 'Ereignis'}</DialogTitle>
          {e && (
            <DialogDescription>
              {e.host?.name ?? 'Unbekannter Host'} · {e.instance.name} · seit {formatDateTime(e.startedAt)}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogBody className="grid gap-4">
          {!e ? (
            <Skeleton className="h-40" />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <SeverityBadge severity={e.severity} />
                {e.status === 'PROBLEM' ? <Badge variant="destructive">Aktiv</Badge> : <Badge variant="success">Behoben</Badge>}
                {e.acknowledged && <Badge variant="secondary">Bestätigt</Badge>}
                <span className="text-muted-foreground">Dauer {duration(e.startedAt, e.resolvedAt)}</span>
                <a href={e.zabbixUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-primary hover:underline">
                  In {e.instance.kind === 'NETDOC' ? 'NetDoc' : 'Zabbix'} öffnen <ExternalLink className="size-3" />
                </a>
              </div>
              <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
                <dt className="text-muted-foreground">Kunde</dt>
                <dd>{e.customer ? <Link to={`/customers/${e.customer.id}`} className="hover:underline" onClick={onClose}>{e.customer.name}</Link> : '–'}</dd>
                <dt className="text-muted-foreground">Ticket</dt>
                <dd>
                  {e.ticket ? (
                    <Link to={`/tickets/${e.ticket.id}`} className="text-primary hover:underline" onClick={onClose}>
                      {e.ticket.number}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">kein Ticket – {e.ticketDecision}</span>
                  )}
                </dd>
              </dl>
              <div>
                <p className="mb-2 text-sm font-medium">Verlauf</p>
                <ol className="grid gap-2 border-l pl-4">
                  {e.history.map((h, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-medium">{HISTORY_LABELS[h.type] ?? h.type}</span> <span className="text-muted-foreground">· {formatDateTime(h.at)} · {h.by}</span>
                      <p>{h.message}</p>
                    </li>
                  ))}
                </ol>
              </div>
              {e.canAcknowledge && (
                <Field label={`Problem in ${e.instance.kind === 'NETDOC' ? 'NetDoc' : 'Zabbix'} bestätigen`} htmlFor="ack-msg" hint="Die Nachricht erscheint in der Quelle und als interne Notiz im Ticket.">
                  <Textarea id="ack-msg" rows={2} value={message} onChange={(ev) => setMessage(ev.target.value)} placeholder="z. B. Techniker ist informiert" />
                </Field>
              )}
            </>
          )}
        </DialogBody>
        {e?.canAcknowledge && (
          <DialogFooter>
            <Button onClick={() => ack.mutate()} loading={ack.isPending}>
              <CheckCheck /> In {e.instance.kind === 'NETDOC' ? 'NetDoc' : 'Zabbix'} bestätigen
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Monitoring-Ereignisse zu einem Ticket oder Gerät. */
export function MonitoringCard({ ticketId, deviceId }: { ticketId?: string; deviceId?: string }) {
  const { can } = useAuth();
  const [open, setOpen] = useState<string | null>(null);
  const query = ticketId ? `ticketId=${ticketId}` : `deviceId=${deviceId}`;
  const { data } = useQuery({
    queryKey: ['monitoring', 'events', 'for', query],
    queryFn: () => api<Paginated<MonitoringEventItem>>(`/monitoring/events?${query}&pageSize=20`),
    enabled: can('monitoring.read'),
  });
  if (!can('monitoring.read') || !data || data.items.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Activity className="size-4" /> Monitoring
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        {data.items.map((e) => (
          <button key={e.id} type="button" onClick={() => setOpen(e.id)} className="rounded-md border p-2.5 text-left text-sm transition-colors hover:bg-muted/60">
            <span className="flex flex-wrap items-center gap-2">
              <SeverityBadge severity={e.severity} />
              {e.status === 'PROBLEM' ? <Badge variant="destructive">Aktiv</Badge> : <Badge variant="success">Behoben</Badge>}
              {e.acknowledged && <Badge variant="secondary">Bestätigt</Badge>}
            </span>
            <span className="mt-1 block font-medium">{e.name}</span>
            <span className="block text-muted-foreground">
              {e.host?.name} · {formatDateTime(e.startedAt)} · {duration(e.startedAt, e.resolvedAt)}
            </span>
          </button>
        ))}
      </CardContent>
      <EventDialog eventId={open} onClose={() => setOpen(null)} />
    </Card>
  );
}
